/**
 * Player Character Entity: "Bertahan Dari Anomali"
 * 
 * Implements:
 * - 8-directional free movement with analog direction smoothing
 * - Automatic orientation toward movement, attack, or locked target
 * - 3-Hit normal attack combo (Tebasan 1 -> 2 -> 3 Finisher)
 * - Flash Step with 3 fading ghost afterimages (45%, 25%, 10% opacity)
 * - Crescent Slash energy projectile launcher
 * - Spin Strike 360-degree whirlwind
 * - Special Attack ("Anomaly Break") with hitstop and screen shake
 * - Potion consumption with healing sparkle VFX
 */

import { PLAYER_CONFIG } from "../config/gameConfig";
import { CombatHitbox, ConeShape, CircleShape } from "../combat/HitboxSystem";
import { GameMap } from "../world/GameMap";
import { anomaliAudio } from "../audio/AnomaliAudio";
import { assetManager } from "../engine/AssetManager";

export interface GhostAfterimage {
  x: number;
  y: number;
  facingAngle: number;
  opacity: number;
  initialOpacity: number;
  decayRate: number;
  width: number;
  height: number;
}

export interface CrescentProjectile {
  id: string;
  x: number;
  y: number;
  startX: number;
  startY: number;
  vx: number;
  vy: number;
  angle: number;
  range: number;
  damage: number;
  knockback: number;
  hitEntities: Set<string>;
  active: boolean;
}

export type PlayerActionState =
  | "idle"
  | "move"
  | "attack_1"
  | "attack_2"
  | "attack_3"
  | "flash_step"
  | "crescent_slash"
  | "spin_strike"
  | "special_charge"
  | "special_strike"
  | "hurt"
  | "dead";

export class Player {
  public id: string = "player";
  public x: number;
  public y: number;
  public vx: number = 0;
  public vy: number = 0;
  public facingAngle: number = 0; // in radians (0 = right, PI/2 = down)

  public hp: number = PLAYER_CONFIG.maxHp;
  public maxHp: number = PLAYER_CONFIG.maxHp;
  public energy: number = PLAYER_CONFIG.maxEnergy;
  public maxEnergy: number = PLAYER_CONFIG.maxEnergy;

  public level: number = 24;
  public potionsLeft: number = PLAYER_CONFIG.potion.maxCharges;

  // State machine
  public state: PlayerActionState = "idle";
  public stateTimeMs: number = 0;
  public comboStep: number = 0; // 0, 1, 2
  public comboResetTimerMs: number = 0;
  public attackHitSpawned: boolean = false;

  // Flash Step
  public flashStepActive: boolean = false;
  public flashStepTimerMs: number = 0;
  public flashStepVx: number = 0;
  public flashStepVy: number = 0;
  public afterimages: GhostAfterimage[] = [];

  // Cooldowns (in seconds)
  public cdFlashStep: number = 0;
  public cdCrescent: number = 0;
  public cdSpin: number = 0;
  public cdSpecial: number = 0;
  public cdPotion: number = 0;

  // Invulnerability & Hurt
  public invulnTimeMs: number = 0;
  public hurtFlashMs: number = 0;
  public knockbackVx: number = 0;
  public knockbackVy: number = 0;

  // Active Projectiles & Hitboxes
  public projectiles: CrescentProjectile[] = [];
  public currentHitboxes: CombatHitbox[] = [];

  // Animation cycle
  public walkAnimClock: number = 0;

  constructor(startX: number = 410, startY: number = 490) {
    this.x = startX;
    this.y = startY;
  }

  public isDead(): boolean {
    return this.state === "dead" || this.hp <= 0;
  }

  public isInvulnerable(): boolean {
    return this.invulnTimeMs > 0 || this.flashStepActive;
  }

  /**
   * Handle movement input from joystick or keyboard (normX, normY in [-1, 1])
   */
  public handleMovementInput(normX: number, normY: number, targetAngle: number | null, map: GameMap, dt: number) {
    if (this.isDead()) return;

    // Movement lock during committed attacks
    const isAttacking =
      this.state === "attack_1" ||
      this.state === "attack_2" ||
      this.state === "attack_3" ||
      this.state === "crescent_slash" ||
      this.state === "spin_strike" ||
      this.state === "special_charge" ||
      this.state === "special_strike" ||
      this.state === "flash_step";

    if (isAttacking) {
      // Small attack forward slide for combos
      if (this.state === "attack_1" || this.state === "attack_2" || this.state === "attack_3") {
        const slideSpeed = 40;
        const targetX = this.x + Math.cos(this.facingAngle) * slideSpeed * dt;
        const targetY = this.y + Math.sin(this.facingAngle) * slideSpeed * dt;
        const resolved = map.resolveMovement(this.x, this.y, targetX, targetY, PLAYER_CONFIG.hitRadius);
        this.x = resolved.x;
        this.y = resolved.y;
      }
      return;
    }

    const inputLen = Math.hypot(normX, normY);
    if (inputLen > 0.05) {
      const speed = PLAYER_CONFIG.moveSpeed * Math.min(1, inputLen);
      const moveVx = (normX / inputLen) * speed;
      const moveVy = (normY / inputLen) * speed;

      // Update facing: if target lock is active, prioritize lock target, otherwise face move direction
      if (targetAngle !== null) {
        this.facingAngle = targetAngle;
      } else {
        this.facingAngle = Math.atan2(moveVy, moveVx);
      }

      // Resolve collision with map
      const nextX = this.x + moveVx * dt;
      const nextY = this.y + moveVy * dt;
      const resolved = map.resolveMovement(this.x, this.y, nextX, nextY, PLAYER_CONFIG.hitRadius);
      this.x = resolved.x;
      this.y = resolved.y;

      this.state = "move";
      this.walkAnimClock += dt * 10;
    } else {
      if (targetAngle !== null) {
        this.facingAngle = targetAngle;
      }
      this.state = "idle";
    }
  }

  /**
   * Trigger Normal Attack 3-Hit Combo
   */
  public triggerNormalAttack(targetAngle: number | null) {
    if (this.isDead() || this.flashStepActive) return;
    if (
      this.state === "attack_1" ||
      this.state === "attack_2" ||
      this.state === "attack_3" ||
      this.state === "special_charge" ||
      this.state === "crescent_slash" ||
      this.state === "spin_strike"
    ) {
      return; // currently mid-action
    }

    if (targetAngle !== null) {
      this.facingAngle = targetAngle;
    }

    // Advance combo step if within reset window
    if (this.comboResetTimerMs > 0) {
      this.comboStep = (this.comboStep + 1) % 3;
    } else {
      this.comboStep = 0;
    }

    this.state = (this.comboStep === 0 ? "attack_1" : this.comboStep === 1 ? "attack_2" : "attack_3") as PlayerActionState;
    this.stateTimeMs = 0;
    this.attackHitSpawned = false;
    this.comboResetTimerMs = PLAYER_CONFIG.comboResetWindowMs;

    // Play combo audio
    const sfxName = this.comboStep === 0 ? "slash_1" : this.comboStep === 1 ? "slash_2" : "slash_3";
    anomaliAudio.playSfx(sfxName);
  }

  /**
   * Trigger Flash Step (Dash with ghost afterimages and i-frames)
   */
  public triggerFlashStep(normX: number, normY: number, targetAngle: number | null) {
    if (this.isDead() || this.cdFlashStep > 0) return;

    let dashAngle = this.facingAngle;
    if (Math.hypot(normX, normY) > 0.1) {
      dashAngle = Math.atan2(normY, normX);
    } else if (targetAngle !== null) {
      dashAngle = targetAngle;
    }

    this.facingAngle = dashAngle;
    this.state = "flash_step";
    this.flashStepActive = true;
    this.stateTimeMs = 0;
    this.flashStepTimerMs = PLAYER_CONFIG.flashStep.durationMs;
    this.cdFlashStep = PLAYER_CONFIG.flashStep.cooldown;
    this.invulnTimeMs = PLAYER_CONFIG.flashStep.invulnerabilityMs;

    // Spawn 3 ghost afterimages
    const opacities = PLAYER_CONFIG.flashStep.ghostOpacities;
    const distance = PLAYER_CONFIG.flashStep.distance;
    const speed = distance / (PLAYER_CONFIG.flashStep.durationMs / 1000);
    this.flashStepVx = Math.cos(dashAngle) * speed;
    this.flashStepVy = Math.sin(dashAngle) * speed;

    this.afterimages = [];
    for (let i = 0; i < opacities.length; i++) {
      const stepOffset = ((i + 1) / (opacities.length + 1)) * (distance * 0.45);
      this.afterimages.push({
        x: this.x - Math.cos(dashAngle) * stepOffset,
        y: this.y - Math.sin(dashAngle) * stepOffset,
        facingAngle: dashAngle,
        opacity: opacities[i],
        initialOpacity: opacities[i],
        decayRate: 1.8,
        width: 32,
        height: 38,
      });
    }

    anomaliAudio.playSfx("flash_step");
  }

  /**
   * Trigger Crescent Slash Skill
   */
  public triggerCrescentSlash(targetAngle: number | null) {
    if (this.isDead() || this.cdCrescent > 0 || this.flashStepActive) return;

    if (targetAngle !== null) {
      this.facingAngle = targetAngle;
    }

    this.state = "crescent_slash";
    this.stateTimeMs = 0;
    this.cdCrescent = PLAYER_CONFIG.crescentSlash.cooldown;

    const angle = this.facingAngle;
    const speed = PLAYER_CONFIG.crescentSlash.speed;
    const cfg = PLAYER_CONFIG.crescentSlash;

    // Launch Crescent Projectile
    this.projectiles.push({
      id: `crescent_${Date.now()}`,
      x: this.x + Math.cos(angle) * 22,
      y: this.y + Math.sin(angle) * 22,
      startX: this.x,
      startY: this.y,
      vx: Math.cos(angle) * speed,
      vy: Math.sin(angle) * speed,
      angle,
      range: cfg.range,
      damage: Math.round(PLAYER_CONFIG.baseAttack * cfg.damageMultiplier),
      knockback: cfg.knockback,
      hitEntities: new Set<string>(),
      active: true,
    });

    anomaliAudio.playSfx("crescent_slash");
  }

  /**
   * Trigger Spin Strike (360 whirlwind)
   */
  public triggerSpinStrike() {
    if (this.isDead() || this.cdSpin > 0 || this.flashStepActive) return;

    this.state = "spin_strike";
    this.stateTimeMs = 0;
    this.cdSpin = PLAYER_CONFIG.spinStrike.cooldown;

    const cfg = PLAYER_CONFIG.spinStrike;
    const shape: CircleShape = {
      type: "circle",
      x: this.x,
      y: this.y,
      radius: cfg.radius,
    };

    this.currentHitboxes.push({
      id: `spin_${Date.now()}`,
      sourceId: this.id,
      isPlayer: true,
      shape,
      damage: Math.round(PLAYER_CONFIG.baseAttack * cfg.damageMultiplier),
      knockback: cfg.knockback,
      hitstopMs: cfg.hitstopMs,
      hitEntities: new Set<string>(),
      durationMs: cfg.durationMs,
      createdAt: Date.now(),
    });

    anomaliAudio.playSfx("spin_strike");
  }

  /**
   * Trigger Special Attack ("Anomaly Break")
   */
  public triggerSpecialAttack(targetAngle: number | null) {
    if (this.isDead() || this.cdSpecial > 0 || this.flashStepActive) return;

    if (targetAngle !== null) {
      this.facingAngle = targetAngle;
    }

    this.state = "special_charge";
    this.stateTimeMs = 0;
    this.cdSpecial = PLAYER_CONFIG.specialAttack.cooldown;
  }

  /**
   * Consume Healing Potion
   */
  public usePotion(): boolean {
    if (this.isDead() || this.potionsLeft <= 0 || this.cdPotion > 0 || this.hp >= this.maxHp) {
      return false;
    }

    this.potionsLeft--;
    this.hp = Math.min(this.maxHp, this.hp + PLAYER_CONFIG.potion.healAmount);
    this.cdPotion = PLAYER_CONFIG.potion.cooldown;
    anomaliAudio.playSfx("potion");
    return true;
  }

  /**
   * Take damage from an enemy attack
   */
  public takeDamage(damage: number, knockbackX: number, knockbackY: number): boolean {
    if (this.isDead() || this.isInvulnerable()) return false;

    this.hp = Math.max(0, this.hp - damage);
    this.invulnTimeMs = PLAYER_CONFIG.hurtInvulnerabilityMs;
    this.hurtFlashMs = 200;
    this.knockbackVx = knockbackX;
    this.knockbackVy = knockbackY;

    if (this.hp <= 0) {
      this.state = "dead";
      this.stateTimeMs = 0;
      anomaliAudio.playSfx("game_over");
    } else {
      this.state = "hurt";
      this.stateTimeMs = 0;
      anomaliAudio.playSfx("hit_player");
    }

    return true;
  }

  /**
   * Update Player State Frame
   */
  public update(dt: number, map: GameMap) {
    const dtMs = dt * 1000;
    this.stateTimeMs += dtMs;

    // Cooldown timers
    if (this.cdFlashStep > 0) this.cdFlashStep = Math.max(0, this.cdFlashStep - dt);
    if (this.cdCrescent > 0) this.cdCrescent = Math.max(0, this.cdCrescent - dt);
    if (this.cdSpin > 0) this.cdSpin = Math.max(0, this.cdSpin - dt);
    if (this.cdSpecial > 0) this.cdSpecial = Math.max(0, this.cdSpecial - dt);
    if (this.cdPotion > 0) this.cdPotion = Math.max(0, this.cdPotion - dt);

    if (this.invulnTimeMs > 0) this.invulnTimeMs = Math.max(0, this.invulnTimeMs - dtMs);
    if (this.hurtFlashMs > 0) this.hurtFlashMs = Math.max(0, this.hurtFlashMs - dtMs);
    if (this.comboResetTimerMs > 0) this.comboResetTimerMs = Math.max(0, this.comboResetTimerMs - dtMs);

    // Energy gentle passive regen
    if (this.energy < this.maxEnergy) {
      this.energy = Math.min(this.maxEnergy, this.energy + 8 * dt);
    }

    // Process knockback decay
    if (Math.abs(this.knockbackVx) > 1 || Math.abs(this.knockbackVy) > 1) {
      const nextX = this.x + this.knockbackVx * dt;
      const nextY = this.y + this.knockbackVy * dt;
      const res = map.resolveMovement(this.x, this.y, nextX, nextY, PLAYER_CONFIG.hitRadius);
      this.x = res.x;
      this.y = res.y;
      this.knockbackVx *= Math.max(0, 1 - 12 * dt);
      this.knockbackVy *= Math.max(0, 1 - 12 * dt);
    }

    // Process Flash Step physics & afterimages decay
    if (this.flashStepActive) {
      this.flashStepTimerMs -= dtMs;
      const nextX = this.x + this.flashStepVx * dt;
      const nextY = this.y + this.flashStepVy * dt;
      const res = map.resolveMovement(this.x, this.y, nextX, nextY, PLAYER_CONFIG.hitRadius);
      this.x = res.x;
      this.y = res.y;

      if (this.flashStepTimerMs <= 0) {
        this.flashStepActive = false;
        this.state = "idle";
      }
    }

    for (let i = this.afterimages.length - 1; i >= 0; i--) {
      const ghost = this.afterimages[i];
      ghost.opacity -= ghost.decayRate * dt;
      if (ghost.opacity <= 0) {
        this.afterimages.splice(i, 1);
      }
    }

    // Handle Attack Combo Hitboxes and State transitions
    if (this.state === "attack_1" || this.state === "attack_2" || this.state === "attack_3") {
      const currentComboCfg = PLAYER_CONFIG.combo[this.comboStep];
      const totalAttackTime = currentComboCfg.startupMs + currentComboCfg.activeMs + currentComboCfg.recoveryMs;

      // Spawn active hitbox once during active window
      if (!this.attackHitSpawned && this.stateTimeMs >= currentComboCfg.startupMs) {
        this.attackHitSpawned = true;
        const cone: ConeShape = {
          type: "cone",
          x: this.x,
          y: this.y,
          radius: currentComboCfg.range,
          angle: this.facingAngle,
          spreadAngle: (currentComboCfg.angleDeg * Math.PI) / 180,
        };

        this.currentHitboxes.push({
          id: `player_combo_${this.comboStep}_${Date.now()}`,
          sourceId: this.id,
          isPlayer: true,
          shape: cone,
          damage: Math.round(PLAYER_CONFIG.baseAttack * currentComboCfg.damageMultiplier),
          knockback: currentComboCfg.knockback,
          hitstopMs: currentComboCfg.hitstopMs,
          hitEntities: new Set<string>(),
          durationMs: currentComboCfg.activeMs,
          createdAt: Date.now(),
        });
      }

      if (this.stateTimeMs >= totalAttackTime) {
        this.state = "idle";
      }
    } else if (this.state === "crescent_slash") {
      if (this.stateTimeMs >= 320) {
        this.state = "idle";
      }
    } else if (this.state === "spin_strike") {
      if (this.stateTimeMs >= PLAYER_CONFIG.spinStrike.durationMs) {
        this.state = "idle";
      }
    } else if (this.state === "special_charge") {
      if (this.stateTimeMs >= PLAYER_CONFIG.specialAttack.chargeMs) {
        this.state = "special_strike";
        this.stateTimeMs = 0;
        const cfg = PLAYER_CONFIG.specialAttack;

        // Frontal devastating strike
        const cone: ConeShape = {
          type: "cone",
          x: this.x,
          y: this.y,
          radius: cfg.strikeRange,
          angle: this.facingAngle,
          spreadAngle: (130 * Math.PI) / 180,
        };

        this.currentHitboxes.push({
          id: `player_special_${Date.now()}`,
          sourceId: this.id,
          isPlayer: true,
          shape: cone,
          damage: Math.round(PLAYER_CONFIG.baseAttack * cfg.damageMultiplier),
          knockback: cfg.knockback,
          hitstopMs: cfg.hitstopMs,
          hitEntities: new Set<string>(),
          durationMs: 180,
          createdAt: Date.now(),
        });

        anomaliAudio.playSfx("special_attack");
      }
    } else if (this.state === "special_strike") {
      if (this.stateTimeMs >= 350) {
        this.state = "idle";
      }
    } else if (this.state === "hurt") {
      if (this.stateTimeMs >= 240) {
        this.state = "idle";
      }
    }

    // Clean expired hitboxes
    const now = Date.now();
    for (let i = this.currentHitboxes.length - 1; i >= 0; i--) {
      const hb = this.currentHitboxes[i];
      if (now - hb.createdAt >= hb.durationMs) {
        this.currentHitboxes.splice(i, 1);
      }
    }

    // Update Crescent Projectiles
    for (let i = this.projectiles.length - 1; i >= 0; i--) {
      const p = this.projectiles[i];
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      const traveled = Math.hypot(p.x - p.startX, p.y - p.startY);
      if (traveled >= p.range || map.isSolid(p.x, p.y, 8)) {
        p.active = false;
        this.projectiles.splice(i, 1);
      }
    }
  }

  /**
   * Render Player Character Sprite, Flash Step Afterimages, Slashes, and VFX
   */
  public render(ctx: CanvasRenderingContext2D, now: number) {
    ctx.save();

    // 1. Render Flash Step Afterimages (Fading translucent ghosts)
    for (const ghost of this.afterimages) {
      ctx.save();
      ctx.globalAlpha = ghost.opacity;
      ctx.translate(ghost.x, ghost.y);
      ctx.rotate(ghost.facingAngle);

      // Cyan ethereal ghost body
      ctx.fillStyle = "#33ccff";
      ctx.beginPath();
      ctx.arc(0, 0, 14, 0, Math.PI * 2);
      ctx.fill();

      // Ghost trail lines
      ctx.strokeStyle = "#88eeff";
      ctx.lineWidth = 2;
      ctx.beginPath();
      ctx.moveTo(-16, -6);
      ctx.lineTo(8, 0);
      ctx.lineTo(-16, 6);
      ctx.stroke();

      ctx.restore();
    }

    // 2. Render Player Shadow
    ctx.fillStyle = "rgba(0, 0, 0, 0.4)";
    ctx.beginPath();
    ctx.ellipse(this.x, this.y + 12, 14, 6, 0, 0, Math.PI * 2);
    ctx.fill();

    // 3. Render Main Player Character
    const isFacingLeft = Math.cos(this.facingAngle) < 0;

    let animKey = "idle";
    let frameIdx = Math.floor(now * 0.012) % 12;

    if (this.state === "move") {
      animKey = "walk";
      frameIdx = Math.floor(this.walkAnimClock * 2) % 12;
    } else if (
      this.state === "attack_1" ||
      this.state === "attack_2" ||
      this.state === "attack_3"
    ) {
      animKey = "attack";
      frameIdx = Math.min(11, Math.floor((this.stateTimeMs / 250) * 12));
    } else if (this.flashStepActive) {
      animKey = "flash_step";
      frameIdx = Math.min(11, Math.floor((this.stateTimeMs / 160) * 12));
    }

    const isFlashing = this.hurtFlashMs > 0 && Math.floor(now * 0.03) % 2 === 0;
    const filter = isFlashing
      ? "brightness(3) saturate(0.2)"
      : this.flashStepActive
      ? "drop-shadow(0 0 8px #00d4ff)"
      : undefined;

    const drawnFromAtlas = assetManager.drawFrame(
      ctx,
      "player",
      animKey,
      frameIdx,
      this.x,
      this.y,
      isFacingLeft,
      1.0,
      filter,
      1.0
    );

    if (!drawnFromAtlas) {
      ctx.save();
      ctx.translate(this.x, this.y);

      // Hurt flash white
      if (this.hurtFlashMs > 0 && Math.floor(now * 0.03) % 2 === 0) {
        ctx.filter = "brightness(3) saturate(0.2)";
      }

      // If Flash stepping, add cyan aura
      if (this.flashStepActive) {
        ctx.shadowColor = "#00d4ff";
        ctx.shadowBlur = 10;
      }

      if (isFacingLeft) {
        ctx.scale(-1, 1);
      }

      // Walk bobbing animation
      const bobY = this.state === "move" ? Math.sin(this.walkAnimClock) * 2 : 0;

      // Body armor (dark tunic matching reference mockup)
      ctx.fillStyle = "#1e2229";
      ctx.fillRect(-7, -14 + bobY, 14, 16);

      // Belt and silver buckle
      ctx.fillStyle = "#4a3b32";
      ctx.fillRect(-7, -2 + bobY, 14, 3);
      ctx.fillStyle = "#c0c8cf";
      ctx.fillRect(-2, -2 + bobY, 4, 3);

      // Legs / Boots
      ctx.fillStyle = "#14171c";
      const legSwing = this.state === "move" ? Math.sin(this.walkAnimClock) * 4 : 0;
      ctx.fillRect(-6 + legSwing, 2 + bobY, 5, 10);
      ctx.fillRect(1 - legSwing, 2 + bobY, 5, 10);

      // Flowing Red Scarf / Cape (iconic hero identity from image)
      const scarfFlow = Math.sin(now * 0.012) * 4;
      ctx.fillStyle = "#d12b2b";
      ctx.beginPath();
      ctx.moveTo(-4, -12 + bobY);
      ctx.lineTo(-18 + scarfFlow, -6 + bobY);
      ctx.lineTo(-14 + scarfFlow, -14 + bobY);
      ctx.closePath();
      ctx.fill();

      // Head / Face
      ctx.fillStyle = "#f5d3b3";
      ctx.beginPath();
      ctx.arc(0, -18 + bobY, 7, 0, Math.PI * 2);
      ctx.fill();

      // Dark Spiky Hair
      ctx.fillStyle = "#221c1a";
      ctx.beginPath();
      ctx.arc(0, -20 + bobY, 7.5, Math.PI, Math.PI * 2);
      ctx.lineTo(8, -18 + bobY);
      ctx.lineTo(6, -24 + bobY);
      ctx.lineTo(2, -27 + bobY);
      ctx.lineTo(-4, -25 + bobY);
      ctx.lineTo(-8, -19 + bobY);
      ctx.closePath();
      ctx.fill();

      // Glowing Blue/Silver Sword
      ctx.save();
      let swordAngle = 0.4;
      const swordX = 6;
      const swordY = -6 + bobY;

      if (this.state === "attack_1") {
        swordAngle = -0.6 + (this.stateTimeMs / 180) * 1.6;
      } else if (this.state === "attack_2") {
        swordAngle = 1.2 - (this.stateTimeMs / 180) * 2.0;
      } else if (this.state === "attack_3") {
        swordAngle = -1.2 + (this.stateTimeMs / 240) * 2.8;
      } else if (this.state === "special_charge") {
        swordAngle = -1.4 + Math.sin(now * 0.04) * 0.2;
      }

      ctx.translate(swordX, swordY);
      ctx.rotate(swordAngle);

      // Sword Hilt
      ctx.fillStyle = "#c29342";
      ctx.fillRect(-2, -2, 4, 4);

      // Sword Blade (Cyan/Silver glowing edge)
      ctx.shadowColor = "#44ccff";
      ctx.shadowBlur = 6;
      ctx.fillStyle = "#d8f2ff";
      ctx.fillRect(2, -1.5, 20, 3);
      ctx.fillStyle = "#88e0ff";
      ctx.fillRect(4, -0.5, 16, 1);
      ctx.restore();

      ctx.restore(); // Character restore
    }

    // 4. Render Attack Slash Arcs & VFX (100% Asset-driven from Texture Atlas)
    if (this.state === "attack_1" || this.state === "attack_2" || this.state === "attack_3") {
      const combo = PLAYER_CONFIG.combo[this.comboStep];
      const progress = Math.min(1.0, this.stateTimeMs / (combo.startupMs + combo.activeMs));
      const vfxName = this.comboStep === 0 ? "slash_1" : this.comboStep === 1 ? "slash_2" : "slash_3";
      assetManager.drawVfxFrame(ctx, vfxName, progress, this.x, this.y, this.facingAngle, 1.4, 1.0, true);
    } else if (this.state === "spin_strike") {
      // 360-degree whirlwind ring (100% Asset-driven)
      const progress = Math.min(1.0, this.stateTimeMs / 380);
      assetManager.drawVfxFrame(ctx, "spin", progress, this.x, this.y, 0, 1.8, 1.0, true);
    } else if (this.state === "special_charge") {
      // Gathering anime energy particles (100% Asset-driven)
      const chargeRatio = Math.min(1.0, this.stateTimeMs / PLAYER_CONFIG.specialAttack.chargeMs);
      assetManager.drawVfxFrame(ctx, "spark", chargeRatio, this.x, this.y, now * 0.005, 1.3, 0.95, true);
    } else if (this.state === "special_strike") {
      // Anomaly Break shockwave burst (100% Asset-driven)
      const burstProgress = Math.min(1.0, this.stateTimeMs / 250);
      assetManager.drawVfxFrame(ctx, "burst", burstProgress, this.x, this.y, 0, 2.5, 1.0, true);
    }

    // 5. Render Crescent Projectiles (100% Asset-driven)
    for (const p of this.projectiles) {
      assetManager.drawVfxFrame(
        ctx,
        "crescent",
        (now * 0.008) % 1.0,
        p.x,
        p.y,
        p.angle,
        1.4,
        1.0,
        true
      );
    }

    // 6. Render Potion Healing Sparkle VFX (100% Asset-driven)
    const potionWindow = PLAYER_CONFIG.potion.cooldown - this.cdPotion;
    if (potionWindow >= 0 && potionWindow <= 0.8) {
      const healProgress = potionWindow / 0.8;
      assetManager.drawVfxFrame(ctx, "heal", healProgress, this.x, this.y, 0, 1.4, 0.95, true);
    }

    ctx.restore();
  }
}
